import { z } from 'zod';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';
import { normalizeDecimal } from '@/shared/utils/number';
import type {
  ApiSplitTarget,
  ApiSplitTargetAllocation,
  ApiSplitsSavePostBody,
} from '@/shared/types/api/splits';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';
import type { ApiParcelsProductsGetItem } from '@/shared/types/api/parcels-products';

// One per parcel product: how many units of it go to this target.
export const splitAllocationSchema = z.object({
  product_id: z.string(),
  quantity: z.number({ invalid_type_error: 'splits:errors.quantityInvalid' }).int().min(0),
});

export const splitTargetSchema = z.object({
  // Present when editing a saved target; absent for newly added ones.
  id: z.string().optional(),
  pinfl: z
    .string()
    .trim()
    .length(env.pinflLength, 'splits:errors.pinflLength')
    .regex(/^\d+$/u, 'splits:errors.pinflDigitsOnly'),
  last_name: z
    .string()
    .trim()
    .min(2, 'splits:errors.lastNameRequired')
    .max(60, 'splits:errors.tooLong'),
  first_name: z
    .string()
    .trim()
    .min(2, 'splits:errors.firstNameRequired')
    .max(60, 'splits:errors.tooLong'),
  middle_name: z
    .string()
    .trim()
    .min(2, 'splits:errors.middleNameRequired')
    .max(60, 'splits:errors.tooLong'),
  phone_number: z
    .string()
    .trim()
    .min(7, 'splits:errors.phoneTooShort')
    .max(20, 'splits:errors.tooLong')
    .regex(/^\+?[0-9\s()-]+$/u, 'splits:errors.phoneInvalid'),
  passport_series: z
    .string()
    .min(1, 'splits:errors.passportRequired')
    .regex(/^[A-Z]{2}[0-9]{7}$/, 'splits:errors.passportInvalid'),
  address_name: z
    .string()
    .trim()
    .min(2, 'splits:errors.addressNameRequired')
    .max(120, 'splits:errors.tooLong'),
  region: z.enum(REGION_VALUES, { required_error: 'splits:errors.regionRequired' }),
  city: z
    .string()
    .trim()
    .min(2, 'splits:errors.cityRequired')
    .max(80, 'splits:errors.tooLong'),
  street: z
    .string()
    .trim()
    .min(2, 'splits:errors.streetRequired')
    .max(120, 'splits:errors.tooLong'),
  building: z
    .string()
    .trim()
    .min(1, 'splits:errors.buildingRequired')
    .max(40, 'splits:errors.tooLong'),
  apartment: z.string().trim().max(40, 'splits:errors.tooLong').optional().or(z.literal('')),
  additional_info: z
    .string()
    .trim()
    .max(500, 'splits:errors.tooLong')
    .optional()
    .or(z.literal('')),
  // Auto-generated via POST /packages/next-name; the input is disabled, so a
  // non-empty value means the user pressed "generate".
  package_name: z.string().trim().min(1, 'splits:errors.packageNameRequired'),
  // Weight + unit are no longer entered on the client (warehouse/admin set the
  // real weight later). They stay in the form state with safe defaults so the
  // save payload keeps its shape; no client-side validation is needed.
  package_weight: z.string().trim(),
  package_weight_unit: z.enum(['kg', 'g'], { required_error: 'splits:errors.weightUnitRequired' }),
  allocations: z.array(splitAllocationSchema),
});

export const splitTargetsFormSchema = z
  .object({
    targets: z.array(splitTargetSchema).min(1, 'splits:errors.targetsMin'),
  })
  // A PINFL may not be reused across recipients. Group the non-empty trimmed
  // PINFLs and flag every member of any group that occurs more than once, so
  // each offending recipient surfaces its own error. Empty/whitespace values
  // are skipped (length/regex rules already cover those).
  .superRefine((value, ctx) => {
    const groups = new Map<string, number[]>();
    value.targets.forEach((target, index) => {
      const pinfl = (target.pinfl ?? '').trim();
      if (!pinfl) return;
      const indices = groups.get(pinfl);
      if (indices) indices.push(index);
      else groups.set(pinfl, [index]);
    });
    for (const indices of groups.values()) {
      if (indices.length < 2) continue;
      for (const index of indices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'splits:errors.pinflDuplicate',
          path: ['targets', index, 'pinfl'],
        });
      }
    }
  });

export type SplitAllocationFormValue = z.infer<typeof splitAllocationSchema>;
export type SplitTargetFormValues = z.infer<typeof splitTargetSchema>;
export type SplitTargetsFormValues = z.infer<typeof splitTargetsFormSchema>;

// Build the product-aligned allocation list for a target, carrying over any
// previously allocated quantities (0 by default).
export const allocationsForProducts = (
  products: ApiParcelsProductsGetItem[],
  existing?: ApiSplitTargetAllocation[],
): SplitAllocationFormValue[] =>
  products.map((p) => ({
    product_id: p.id,
    quantity: existing?.find((a) => a.product_id === p.id)?.quantity ?? 0,
  }));

export const buildEmptyTarget = (
  products: ApiParcelsProductsGetItem[],
): SplitTargetFormValues => ({
  pinfl: '',
  last_name: '',
  first_name: '',
  middle_name: '',
  phone_number: '',
  passport_series: '',
  address_name: '',
  region: 'TASHKENT_CITY',
  city: '',
  street: '',
  building: '',
  apartment: '',
  additional_info: '',
  package_name: '',
  package_weight: '0',
  package_weight_unit: 'kg',
  allocations: allocationsForProducts(products),
});

export const mapTargetToFormValues = (
  t: ApiSplitTarget,
  products: ApiParcelsProductsGetItem[],
): SplitTargetFormValues => ({
  id: t.id,
  pinfl: t.pinfl,
  last_name: t.last_name,
  first_name: t.first_name,
  middle_name: t.middle_name ?? '',
  phone_number: t.phone_number,
  passport_series: t.passport_series,
  address_name: t.address_name,
  region: t.region,
  city: t.city,
  street: t.street,
  building: t.building,
  apartment: t.apartment ?? '',
  additional_info: t.additional_info ?? '',
  package_name: t.package_name ?? '',
  package_weight: t.package_weight ?? '0',
  package_weight_unit: t.package_weight_unit ?? 'kg',
  allocations: allocationsForProducts(products, t.allocations),
});

export const mapParcelToTargetFormValues = (
  p: ApiParcelsGetItem,
  products: ApiParcelsProductsGetItem[],
): SplitTargetFormValues => ({
  pinfl: p.pinfl,
  last_name: p.last_name,
  first_name: p.first_name,
  middle_name: p.middle_name ?? '',
  phone_number: p.phone_number,
  passport_series: p.passport_series,
  address_name: p.address_name,
  region: p.region,
  city: p.city,
  street: p.street,
  building: p.building,
  apartment: p.apartment ?? '',
  additional_info: p.additional_info ?? '',
  package_name: '',
  package_weight: '0',
  package_weight_unit: 'kg',
  allocations: allocationsForProducts(products),
});

export const mapFormValuesToTargetsPayload = (
  values: SplitTargetsFormValues,
): ApiSplitsSavePostBody => ({
  targets: values.targets.map((target) => ({
    ...(target.id ? { id: target.id } : {}),
    pinfl: target.pinfl,
    first_name: target.first_name,
    last_name: target.last_name,
    middle_name: target.middle_name ?? '',
    phone_number: target.phone_number,
    passport_series: target.passport_series,
    address_name: target.address_name,
    region: target.region,
    city: target.city,
    street: target.street,
    building: target.building,
    apartment: target.apartment || undefined,
    additional_info: target.additional_info || undefined,
    package_name: target.package_name,
    package_weight: normalizeDecimal(target.package_weight),
    package_weight_unit: target.package_weight_unit,
    // Omit zero lines; the server validates the per-product sum either way.
    allocations: target.allocations
      .filter((a) => a.quantity > 0)
      .map((a) => ({ product_id: a.product_id, quantity: a.quantity })),
  })),
});

// ---------------------------------------------------------------------------
// Exact product distribution validation
// ---------------------------------------------------------------------------

export type DistributionMismatch = {
  product_id: string;
  full_name: string;
  expected: number;
  allocated: number;
};

// Every product's allocations across ALL targets must sum EXACTLY to its
// quantity (not less, not more). Returns the offending products (empty = OK).
export const findDistributionMismatches = (
  targets: SplitTargetsFormValues['targets'],
  products: ApiParcelsProductsGetItem[],
): DistributionMismatch[] => {
  const mismatches: DistributionMismatch[] = [];
  for (const product of products) {
    const allocated = targets.reduce(
      (sum, target) =>
        sum +
        target.allocations
          .filter((a) => a.product_id === product.id)
          .reduce((s, a) => s + (Number.isFinite(a.quantity) ? a.quantity : 0), 0),
      0,
    );
    if (allocated !== product.quantity) {
      mismatches.push({
        product_id: product.id,
        full_name: product.full_name,
        expected: product.quantity,
        allocated,
      });
    }
  }
  return mismatches;
};
